import { useConfig, useValidation, type Rules } from '@seek/libs-shared';
import { Alert, Heading, Stack, Text } from 'braid-design-system';
import { useEffect } from 'react';
import {
  CURRENT_PAGE_WORK_LIFE_BALANCE,
  trackContinuePressed,
  trackDisplayPage,
  trackSkipQuestionPressed,
} from '../../../shared/tracking/writeAReview/trackingEvents';
import { ReviewPageLayoutNoStepper } from '../../components/ReviewPageLayout/ReviewPageLayoutNoStepper';
import type { Rating } from '../../components/StarSelector/StarSelector';
import { WorkLifeBalanceRatingQuestion } from '../../questions/RatingQuesions/WorkLifeBalanceRatingQuestion';
import { WorkLifeBalanceDetailsQuestion } from '../../questions/SimpleQuestions/WorkLifeBalanceDetailsQuestion';
import {
  ExperimentTwoProgress,
  type PageProps,
  type ReviewPayload,
} from '../../type';
import { validationRules } from '../../validation/validationRules';

type Props = PageProps & {
  isSubmitting: boolean;
};

export const WorkLifeBalancePage = ({
  review,
  reviewFormContext,
  setPage,
  setter,
  pageNumber,
  isSubmitting,
  onSubmit,
}: Props) => {
  const config = useConfig();

  useEffect(() => {
    trackDisplayPage({
      config,
      context: reviewFormContext,
      currentPage: CURRENT_PAGE_WORK_LIFE_BALANCE,
      variant: reviewFormContext.variant ?? '1.0.0',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fields = {
    workLifeBalanceDescription: review?.workLifeBalanceDescription,
  };

  const { workLifeBalanceDescription } = validationRules(config.language);

  const rules = {
    workLifeBalanceDescription,
  } as Rules<typeof fields>;

  const {
    getTone,
    getMessage,
    handleValueChangeWithValidation,
    validateWithErrors,
  } = useValidation<ReviewPayload>(fields, rules);

  const handleOnContinue = () => {
    const { isValid } = validateWithErrors();

    if (onSubmit) {
      onSubmit();
    }

    trackContinuePressed({
      config,
      context: reviewFormContext,
      validation: isValid ? 'success' : 'mandatory_questions_not_answered',
      currentPage: CURRENT_PAGE_WORK_LIFE_BALANCE,
      variant: reviewFormContext.variant ?? '1.0.0',
    });

    if (!isValid) {
      return;
    }

    setPage(ExperimentTwoProgress.PerksAndBenefits);
  };

  const handleOnSkip = () => {
    trackSkipQuestionPressed({
      config,
      context: reviewFormContext,
      currentPage: CURRENT_PAGE_WORK_LIFE_BALANCE,
      variant: reviewFormContext.variant ?? '1.0.0',
    });

    setPage(ExperimentTwoProgress.PerksAndBenefits);
  };

  const handleOnChange =
    (updateProperty: keyof ReviewPayload) => (value: Rating) => {
      setter({
        ...review,
        [updateProperty]: value,
      });
    };

  return (
    <>
      <Alert tone="positive">
        <Stack space="medium">
          <Text weight="strong">Submitted!</Text>
          <Text>
            Your experience helps others! You can add more detail in your review
            below.
          </Text>
        </Stack>
      </Alert>
      <ReviewPageLayoutNoStepper
        progress={pageNumber}
        onContinue={handleOnContinue}
        onSkip={handleOnSkip}
        isContinueLoading={isSubmitting}
      >
        <Stack space="large">
          <Heading level="1">Work life balance</Heading>
          <WorkLifeBalanceRatingQuestion
            onChange={handleOnChange('workLifeBalanceRating')}
            value={review?.workLifeBalanceRating}
            title="Rate your work/life balance"
            isRequired={true}
          />
          <WorkLifeBalanceDetailsQuestion
            onChange={handleValueChangeWithValidation({
              setter,
              validatorProperty: 'workLifeBalanceDescription',
              previousValue: review,
            })}
            tone={getTone('workLifeBalanceDescription')}
            message={getMessage('workLifeBalanceDescription')}
            value={review?.workLifeBalanceDescription}
          />
        </Stack>
      </ReviewPageLayoutNoStepper>
    </>
  );
};
